// src/components/CategoryPickerSheet.jsx
//
// Bottom sheet выбора категории товара.
// Заменяет нативный <select> с <optgroup>, который на Android рендерился
// системным списком и выглядел чужеродно (плюс дерево имитировалось тире).
//
// Иерархия показывается визуально: родитель — заголовок-строка (кликабельная,
// если у неё есть свои товары), подкатегории — с отступом и точкой.
//
// props:
//   categories — плоский список [{ id, name, parentId }]
//   value      — выбранный id ('' = без категории)
//   onPick(id) — выбор
//   onClose()  — закрыть

import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export default function CategoryPickerSheet({ categories = [], value = '', onPick, onClose }) {
  const { t } = useTranslation();

  // Строим дерево: корни + дети
  const roots = categories.filter(c => !c.parentId);
  const childrenOf = (id) => categories.filter(c => c.parentId === id);

  const pick = (id) => {
    onPick?.(id);
    onClose?.();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'var(--bg)',
          borderRadius: '16px 16px 0 0',
          padding: 20,
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Шапка */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{
            flex: 1,
            fontSize: 17, fontWeight: 800,
            letterSpacing: '-0.02em',
            fontFamily: 'var(--font-display)',
          }}>
            {t('products.category')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--fg-muted)', padding: 4,
              display: 'flex', alignItems: 'center',
            }}
            aria-label={t('common.close')}
          >
            <Icon name="x" size={20} stroke={2.5} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', margin: '0 -20px', padding: '0 20px' }}>
          {/* Без категории */}
          <Row
            label={t('products.categoryNone')}
            selected={value === ''}
            onClick={() => pick('')}
            muted
          />

          {roots.map(root => {
            const kids = childrenOf(root.id);
            return (
              <div key={root.id} style={{ marginTop: 6 }}>
                {/* Сама родительская категория — выбираемая */}
                <Row
                  label={root.name}
                  selected={value === root.id}
                  onClick={() => pick(root.id)}
                  bold
                />
                {/* Подкатегории — с отступом */}
                {kids.map(kid => (
                  <Row
                    key={kid.id}
                    label={kid.name}
                    selected={value === kid.id}
                    onClick={() => pick(kid.id)}
                    indent
                  />
                ))}
              </div>
            );
          })}

          {roots.length === 0 && (
            <p style={{
              fontSize: 13, color: 'var(--fg-muted)',
              textAlign: 'center', padding: '24px 0',
              lineHeight: 1.5,
            }}>
              {t('categories.noCategories')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, selected, onClick, indent, bold, muted }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 14px',
        paddingLeft: indent ? 34 : 14,
        background: selected ? 'var(--bg-subtle)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--r-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        marginBottom: 2,
      }}
    >
      {indent && (
        <span style={{
          width: 5, height: 5, borderRadius: '50%',
          background: selected ? 'var(--brand-primary)' : 'var(--fg-faint)',
          flexShrink: 0, marginLeft: -14,
        }} />
      )}
      <span style={{
        flex: 1,
        fontSize: 15,
        fontWeight: bold ? 700 : 500,
        color: muted && !selected ? 'var(--fg-muted)' : 'var(--fg)',
        fontFamily: bold ? 'var(--font-display)' : undefined,
      }}>
        {label}
      </span>
      {selected && (
        <Icon name="check" size={18} stroke={2.5} color="var(--brand-primary)" />
      )}
    </button>
  );
}
